import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language].contact;
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (will be replaced with actual API call)
    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast({
        title: t.successTitle,
        description: t.successDesc,
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#FAFAF8]">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-20 fade-in">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-4">{t.sectionLabel}</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-light text-[#2c2c2c] mb-6">
            {t.title}
          </h2>
          <div className="w-24 h-0.5 bg-[#8B7355] mx-auto mb-8"></div>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-['Cormorant_Garamond'] font-light text-[#2c2c2c] mb-6">
                {t.getInTouch}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8">
                {t.intro}
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#8B7355] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c] mb-1">{t.officeLocation}</h4>
                  <p className="text-gray-600 text-sm whitespace-pre-line">
                    {t.address}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#8B7355] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c] mb-1">{t.phone}</h4>
                  <p className="text-gray-600 text-sm">{t.phoneNumber}</p>
                  <p className="text-gray-500 text-xs mt-1">{t.hours}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#8B7355] rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2c2c2c] mb-1">{t.email}</h4>
                  <p className="text-gray-600 text-sm">{t.emailAddress}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 md:p-10 rounded-sm shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-[#2c2c2c] mb-2 block">
                  {t.nameLabel} *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-none border-gray-300 focus:border-[#8B7355] focus:ring-[#8B7355]"
                  placeholder={t.namePlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[#2c2c2c] mb-2 block">
                  {t.emailLabel} *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-none border-gray-300 focus:border-[#8B7355] focus:ring-[#8B7355]"
                  placeholder={t.emailPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#2c2c2c] mb-2 block">
                  {t.phoneLabel} *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full rounded-none border-gray-300 focus:border-[#8B7355] focus:ring-[#8B7355]"
                  placeholder={t.phonePlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-[#2c2c2c] mb-2 block">
                  {t.messageLabel} *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full rounded-none border-gray-300 focus:border-[#8B7355] focus:ring-[#8B7355] resize-none"
                  placeholder={t.messagePlaceholder}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#8B7355] hover:bg-[#6d5a42] text-white py-6 rounded-none transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  t.sending
                ) : (
                  <>
                    {t.sendBtn}
                    <Send className="ml-2" size={18} />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;